import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  FileText, ArrowsLeftRight, House, ShieldCheck, User,
  IdentificationBadge, ArrowLeft, ArrowUp,
} from "@phosphor-icons/react";

import { StudentInfoSection } from "@/components/admissions/StudentInfoSection";
import { AddressSection } from "@/components/admissions/AddressSection";
import { ParentSection } from "@/components/admissions/ParentSection";
import { AllocationSection } from "@/components/admissions/AllocationSection";
import { StudentPhotoStack } from "@/components/admissions/StudentPhotoStack";
import { DocumentsTab, DOC_TYPE_LABELS } from "@/components/admissions/DocumentsTab";
import { PromoteDialog } from "@/components/admissions/PromoteDialog";

const STATUS_BADGE_CLASS = {
  active: "border-[var(--vs-success)]/40 text-[var(--vs-success)] bg-[var(--vs-success)]/10",
  draft: "border-[var(--vs-warning)]/40 text-[var(--vs-warning)] bg-[var(--vs-warning)]/10",
};
const DEFAULT_BADGE_CLASS = "border-[var(--vs-border)]";

const FORM_KEYS = [
  "first_name", "middle_name", "last_name", "roll_number", "dob", "gender",
  "aadhar", "phone", "email",
  "address", "city", "state", "country", "pincode",
  "guardian_name", "guardian_phone",
  "parent_father_name", "parent_mother_name", "parent_phone", "parent_email",
  "parent_address", "parent_city", "parent_state", "parent_country", "parent_pincode",
  "parent_aadhar",
  "is_single_parent", "single_parent_type",
  "hostel_id", "school_id", "class_name", "section", "room_no", "bed_no", "status",
  // Batch 25 — medical + APAAR + diversity
  "height_cm", "weight_kg", "blood_group", "apaar_id",
  "category", "mother_tongue", "physically_challenged", "physical_challenge_type",
  // Batch 27 — custom discount %
  "discount_pct", "discount_reason", "special_requirement", "is_employee_child",
  "discount_funded_by_donor_id",
];

export default function StudentEdit() {
  const { studentId } = useParams();
  const { user } = useAuth();

  const [student, setStudent] = useState(null);
  const [hostels, setHostels] = useState([]);
  const [schools, setSchools] = useState([]);
  const [docs, setDocs] = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({});
  const [docForm, setDocForm] = useState({ doc_type: "birth_cert", description: "" });
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoteForm, setPromoteForm] = useState({ to_class: "", section: "" });

  const refreshStudent = () =>
    api.get(`/students/${studentId}`).then((r) => {
      setStudent(r.data);
      const next = {};
      FORM_KEYS.forEach((k) => { next[k] = r.data[k] || ""; });
      next.country = r.data.country || "India";
      next.parent_country = r.data.parent_country || "India";
      next.gender = r.data.gender || "male";
      next.status = r.data.status || "active";
      setForm(next);
    });

  const refreshDocs = () =>
    api.get(`/students/${studentId}/documents`).then((r) => setDocs(r.data)).catch(() => setDocs([]));

  useEffect(() => {
    refreshStudent();
    refreshDocs();
    api.get("/admin/hostels").then((r) => setHostels(r.data)).catch(() => {});
    api.get("/admin/schools").then((r) => setSchools(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSave = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.patch(`/students/${studentId}`, payload);
      toast.success("Student updated");
      refreshStudent();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const onPhotoUpload = async (kind, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (kind === "student") {
        await api.post(`/students/${studentId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Student photo uploaded");
        refreshStudent();
      } else {
        const docType = kind === "parent" ? "parent_photo" : "guardian_photo";
        fd.append("doc_type", docType);
        fd.append("description", `${kind} photo`);
        await api.post(`/students/${studentId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`${kind === "parent" ? "Parent" : "Guardian"} photo uploaded`);
        refreshStudent();
        refreshDocs();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const onDocUpload = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docForm.doc_type);
      if (docForm.description) fd.append("description", docForm.description);
      await api.post(`/students/${studentId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${DOC_TYPE_LABELS[docForm.doc_type]} uploaded`);
      refreshDocs();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const onDeleteDoc = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/students/${studentId}/documents/${docId}`);
      toast.success("Document removed");
      refreshDocs();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(`/students/${studentId}/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const onPromote = async (e) => {
    e?.preventDefault();
    if (!promoteForm.to_class.trim()) {
      toast.error("Pick a target class");
      return;
    }
    try {
      const payload = { to_class: promoteForm.to_class.trim() };
      if (promoteForm.section) payload.section = promoteForm.section;
      await api.post(`/school/promote-student/${studentId}`, payload);
      toast.success(`Promoted to Class ${payload.to_class}${payload.section ? `-${payload.section}` : ""}`);
      setPromoteOpen(false);
      setPromoteForm({ to_class: "", section: "" });
      refreshStudent();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const canPromote = ["super_admin", "school_admin"].includes(user?.role);
  const canEdit = ["super_admin", "hostel_admin", "school_admin"].includes(user?.role);

  if (!student) {
    return <DashboardLayout title="Loading…"><div className="text-sm text-[var(--vs-text-secondary)]">Fetching student…</div></DashboardLayout>;
  }

  return (
    <DashboardLayout
      title={`${student.name} (${student.vs_id})`}
      subtitle="Manage allocation, parent info, photo and documents."
    >
      <div className="flex items-center justify-between mb-4">
        <Link to="/app/students" className="inline-flex items-center gap-2 text-sm text-[var(--vs-text-secondary)] hover:text-[var(--vs-secondary)]" data-testid="back-to-students">
          <ArrowLeft size={14} /> Back to students
        </Link>
        <Badge
          variant="outline"
          className={STATUS_BADGE_CLASS[student.status] || DEFAULT_BADGE_CLASS}
        >
          {student.status}
        </Badge>
      </div>

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile" data-testid="tab-profile"><User size={12} className="mr-1.5" />Profile & Parent</TabsTrigger>
          <TabsTrigger value="allocation" data-testid="tab-allocation"><House size={12} className="mr-1.5" />Allocation</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents"><FileText size={12} className="mr-1.5" />Documents ({docs.length})</TabsTrigger>
        </TabsList>

        {/* Profile + Parent */}
        <TabsContent value="profile">
          <form onSubmit={onSave} className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
            <StudentPhotoStack student={student} onPhotoUpload={onPhotoUpload} />

            <div className="lg:col-span-2 vs-card p-5 space-y-5">
              <div>
                <div className="eyebrow mb-3">Student info</div>
                <StudentInfoSection form={form} set={set} idPrefix="edit" showStatus />
              </div>

              <div className="vs-divider" />

              <div>
                <div className="eyebrow mb-3">Address</div>
                <AddressSection form={form} set={set} testIdPrefix="edit" />
              </div>

              <div className="vs-divider" />

              <div>
                <div className="eyebrow mb-3"><IdentificationBadge size={12} className="inline mr-1" />Parent / Guardian — full information</div>
                <ParentSection form={form} set={set} idPrefix="edit" />
              </div>

              <div className="flex items-center gap-3 pt-2">
                <Button data-testid="save-profile" type="submit" disabled={saving || !canEdit} className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)]">
                  {saving ? "Saving…" : "Save changes"}
                </Button>
                <div className="flex items-center gap-1.5 text-xs font-mono text-[var(--vs-secondary)]">
                  <ShieldCheck size={14} weight="fill" /> AES-256 PII at rest
                </div>
              </div>
            </div>
          </form>
        </TabsContent>

        {/* Allocation */}
        <TabsContent value="allocation">
          <form onSubmit={onSave} className="vs-card p-5 mt-4 max-w-3xl space-y-4">
            <div className="flex items-center justify-between">
              <div className="eyebrow"><ArrowsLeftRight size={12} className="inline mr-1" />Hostel · School · Section · Room · Bed</div>
              {canPromote && student.school_id && student.class_name && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  data-testid="open-promote-btn"
                  onClick={() => { setPromoteForm({ to_class: "", section: student.section || "" }); setPromoteOpen(true); }}
                >
                  <ArrowUp size={12} className="mr-1.5" />
                  Promote (Class {student.class_name}{student.section ? `-${student.section}` : ""})
                </Button>
              )}
            </div>

            <AllocationSection form={form} set={set} hostels={hostels} schools={schools} idPrefix="edit" />

            <div className="flex items-center gap-3 pt-1">
              <Button data-testid="save-allocation" type="submit" disabled={saving || !canEdit} className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)]">
                {saving ? "Saving…" : "Save allocation"}
              </Button>
              <div className="text-[11px] font-mono text-[var(--vs-text-secondary)]">Audit log captures every allocation change.</div>
            </div>
          </form>
        </TabsContent>

        {/* Documents */}
        <TabsContent value="documents">
          <DocumentsTab
            docs={docs}
            docForm={docForm}
            setDocForm={setDocForm}
            onDocUpload={onDocUpload}
            onDeleteDoc={onDeleteDoc}
            downloadDoc={downloadDoc}
            canEdit={canEdit}
          />
        </TabsContent>
      </Tabs>

      <PromoteDialog
        open={promoteOpen}
        onOpenChange={setPromoteOpen}
        student={student}
        form={promoteForm}
        setForm={setPromoteForm}
        onSubmit={onPromote}
      />
    </DashboardLayout>
  );
}
